import * as React from 'react';
import React__default, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { toast, Button, MediaQuery, Alert, Price, Modal, Image, Link, RadioButtonGroupWithContent, Loader } from '@grandvisionhq/common';
import { gql, useLazyQuery } from '@grandvisionhq/graphql';
import * as swcHelpers from '@swc/helpers';
import { Icon } from '@grandvisionhq/icons';
import { Orders, Products, ResponseError, Carts, PaymentTypes } from '@grandvisionhq/api-client';
import { useFeatureToggles, useIntl, useSelector, ApiConfigSelectors, useApiConfig, withIntl } from '@grandvisionhq/state';
import { useDynamicModule, isNil, useInterval, unspaceString, appendQueryParamToUrl } from '@grandvisionhq/utils';
import get from 'lodash/get';
import { Text } from '@grandvisionhq/elements';
import { useLink } from '@grandvisionhq/frk-routing';
import { PhoneNumber, Select, CheckboxWithLabel } from '@grandvisionhq/forms';
import classNames from 'classnames';

var PaymentServiceProvider;
(function(PaymentServiceProvider) {
    PaymentServiceProvider["ADYEN"] = "ADYEN";
    PaymentServiceProvider["CASH_ON_DELIVERY"] = "CASH_ON_DELIVERY";
    PaymentServiceProvider["FAKE"] = "FAKE";
})(PaymentServiceProvider || (PaymentServiceProvider = {}));
var ExpressPaymentMethod;
(function(ExpressPaymentMethod) {
    ExpressPaymentMethod["APPLE_PAY"] = "APPLE_PAY";
    ExpressPaymentMethod["GOOGLE_PAY"] = "GOOGLE_PAY";
    ExpressPaymentMethod["AMAZON_PAY"] = "AMAZON_PAY";
    ExpressPaymentMethod["PAYPAL"] = "PAYPAL";
})(ExpressPaymentMethod || (ExpressPaymentMethod = {}));
var PaymentMethod;
(function(PaymentMethod) {
    PaymentMethod["APPLE_PAY"] = "APPLE_PAY";
    PaymentMethod["BANCONTACT_CARD"] = "BANCONTACT_CARD";
    PaymentMethod["BANCONTACT_MOBILE"] = "BANCONTACT_MOBILE";
    PaymentMethod["CASH_ON_DELIVERY"] = "CASH_ON_DELIVERY";
    PaymentMethod["CREDITCARD"] = "CREDITCARD";
    PaymentMethod["GOOGLE_PAY"] = "GOOGLE_PAY";
    PaymentMethod["FAKE"] = "FAKE";
    PaymentMethod["IDEAL"] = "IDEAL";
    PaymentMethod["KLARNA"] = "KLARNA";
    PaymentMethod["KLARNA_ACCOUNT"] = "KLARNA_ACCOUNT";
    PaymentMethod["MBWAY"] = "MBWAY";
    PaymentMethod["MOBILE_PAY"] = "MOBILE_PAY";
    PaymentMethod["MULTIBANCO"] = "MULTIBANCO";
    PaymentMethod["ONEY"] = "ONEY";
    PaymentMethod["PAYPAL"] = "PAYPAL";
    PaymentMethod["SEPA_DIRECT_DEBIT"] = "SEPA_DIRECT_DEBIT";
    PaymentMethod["SWISH"] = "SWISH";
})(PaymentMethod || (PaymentMethod = {}));
var AdyenBrands;
(function(AdyenBrands) {
    AdyenBrands["VISA"] = 'visa';
    AdyenBrands["MASTERCARD"] = 'mc';
    AdyenBrands["AMEX"] = 'amex';
    AdyenBrands["BANCONTACTMC"] = 'bcmc';
    AdyenBrands["MAESTRO"] = 'maestro';
    AdyenBrands["DANKORT"] = 'dankort';
})(AdyenBrands || (AdyenBrands = {}));

var AdyenPaymentType;
(function(AdyenPaymentType) {
    AdyenPaymentType["Card"] = 'card';
    AdyenPaymentType["Bancontact"] = 'bcmc';
    AdyenPaymentType["BancontactMobile"] = 'bancontactMobile';
    AdyenPaymentType["Sepa"] = 'sepadirectdebit';
    AdyenPaymentType["Swish"] = 'swish';
})(AdyenPaymentType || (AdyenPaymentType = {}));

var PaymentFlow;
(function(PaymentFlow) {
    PaymentFlow["REDIRECT"] = 'Redirect';
    PaymentFlow["ACTIONLESS"] = 'Actionless';
    PaymentFlow["QR_CODE"] = 'QrCode';
    PaymentFlow["UNDEFINED"] = 'Undefined';
})(PaymentFlow || (PaymentFlow = {}));

const getBrandIcon = (brand)=>{
    switch(brand){
        case AdyenBrands.MASTERCARD:
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "mastercard",
                name: "mastercard",
                viewBox: "6 0 36 28",
                width: 36,
                height: 30
            });
        case AdyenBrands.AMEX:
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "amex",
                name: "amex",
                viewBox: "0 0 40 24",
                width: 40,
                height: 24
            });
        case AdyenBrands.MAESTRO:
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "maestro",
                name: "maestro",
                viewBox: "6 0 36 28",
                width: 36,
                height: 30
            });
        case AdyenBrands.DANKORT:
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "dankort",
                name: "dankort",
                viewBox: "0 0 39 19",
                width: 39,
                height: 19
            });
        case AdyenBrands.VISA:
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "visa",
                name: "visa",
                viewBox: "0 7 47 20",
                width: 47,
                height: 30
            });
        case AdyenBrands.BANCONTACTMC:
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "bancontact",
                name: "bancontact",
                width: 54,
                height: 33,
                viewBox: "1 0 54 33"
            });
        default:
            return undefined;
    }
};
const getCreditCardBrandsIcons = (brands)=>brands.reduce((acc, brand)=>[
            ...acc,
            getBrandIcon(brand)
        ]
    , [])
;

const getPaymentMethodIcon = (method)=>{
    switch(method){
        case PaymentMethod.BANCONTACT_CARD:
        case PaymentMethod.BANCONTACT_MOBILE:
            return [
                /*#__PURE__*/ React__default.createElement(Icon, {
                    key: "bancontact",
                    name: "bancontact",
                    width: 54,
                    height: 33,
                    viewBox: "1 0 54 33"
                }), 
            ];
        case PaymentMethod.CASH_ON_DELIVERY:
            return [
                /*#__PURE__*/ React__default.createElement(Icon, {
                    key: "cashOnDelivery",
                    name: "cash-on-delivery"
                })
            ];
        case PaymentMethod.IDEAL:
            return [
                /*#__PURE__*/ React__default.createElement(Icon, {
                    key: "ideal",
                    name: "ideal",
                    viewBox: "0 0 26 23",
                    width: 26,
                    height: 23
                })
            ];
        case PaymentMethod.KLARNA:
        case PaymentMethod.KLARNA_ACCOUNT:
            return [
                /*#__PURE__*/ React__default.createElement(Icon, {
                    key: "klarna",
                    name: "klarna",
                    viewBox: "0 0 45 25",
                    width: 60,
                    height: 33
                })
            ];
        case PaymentMethod.MBWAY:
            return [
                /*#__PURE__*/ React__default.createElement(Icon, {
                    key: "mbway",
                    name: "mbway",
                    viewBox: "18 0 54 48",
                    width: 60,
                    height: 48
                })
            ];
        case PaymentMethod.MOBILE_PAY:
            return [
                /*#__PURE__*/ React__default.createElement(Icon, {
                    key: "mobilepay",
                    name: "mobilepay",
                    viewBox: "0 0 40 26",
                    width: 40,
                    height: 26
                })
            ];
        case PaymentMethod.MULTIBANCO:
            return [
                /*#__PURE__*/ React__default.createElement(Icon, {
                    key: "multibanco",
                    name: "multibanco",
                    viewBox: "-24 0 56 64",
                    width: 56,
                    height: 64
                }), 
            ];
        case PaymentMethod.ONEY:
            return [
                /*#__PURE__*/ React__default.createElement(Icon, {
                    key: "oney",
                    name: "oney",
                    viewBox: "0 0 113.5 36.5",
                    width: 32,
                    height: 24
                })
            ];
        case PaymentMethod.PAYPAL:
            return [
                /*#__PURE__*/ React__default.createElement(Icon, {
                    key: "paypal",
                    name: "paypal",
                    viewBox: "-2 4 48 16",
                    width: 48,
                    height: 24
                })
            ];
        case PaymentMethod.SEPA_DIRECT_DEBIT:
            return [
                /*#__PURE__*/ React__default.createElement(Icon, {
                    key: "sepa",
                    name: "sepa",
                    viewBox: "0 0 32 24",
                    width: 32,
                    height: 24
                })
            ];
        case PaymentMethod.SWISH:
            return [
                /*#__PURE__*/ React__default.createElement(Icon, {
                    key: "swish",
                    name: "swish",
                    viewBox: "10 0 70 34",
                    width: 70,
                    height: 40
                })
            ];
        case PaymentMethod.APPLE_PAY:
            return [
                /*#__PURE__*/ React__default.createElement(Icon, {
                    key: "apple",
                    name: "apple",
                    viewBox: "0 0 32 32",
                    width: 32,
                    height: 32
                })
            ];
        default:
            return undefined;
    }
};

const getBankIcon = (bank)=>{
    switch(bank){
        case 'ASN Bank':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "asn",
                name: "asn",
                viewBox: "7 0 26 26",
                width: 26,
                height: 26
            });
        case 'bunq':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "bunq",
                name: "bunq",
                viewBox: "0 0 40 24",
                width: 40,
                height: 24
            });
        case 'Knab':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "knab",
                name: "knab",
                viewBox: "0 0 39 28",
                width: 39,
                height: 30
            });
        case 'Rabobank':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "rabo",
                name: "rabo",
                viewBox: "7 0 25 30",
                width: 25,
                height: 30
            });
        case 'RegioBank':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "regio",
                name: "regio",
                viewBox: "0 7 47 20",
                width: 47,
                height: 30
            });
        case 'Revolut':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "revolut",
                name: "revolut",
                viewBox: "7 0 28 28",
                width: 28,
                height: 28
            });
        case 'Triodos Bank':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "triodos",
                name: "triodos",
                viewBox: "7 2 25 25",
                width: 25,
                height: 25
            });
        case 'Van Lanschot Bankiers':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "vanLanschot",
                name: "vanLanschot",
                viewBox: "9 0 22 26",
                width: 22,
                height: 26
            });
        case 'ABN':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "abn",
                name: "abn",
                viewBox: "10 2 22 24",
                width: 22,
                height: 24
            });
        case 'ABN Amro':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "abnAmro",
                name: "abnAmro",
                viewBox: "0 0 30 28",
                width: 30,
                height: 28
            });
        case 'ING':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "ing",
                name: "ing",
                viewBox: "0 0 28 22",
                width: 28,
                height: 22
            });
        case 'Yoursafe':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "yourSafe",
                name: "yourSafe",
                viewBox: "0 0 28 25",
                width: 28,
                height: 25
            });
        case 'SNS':
        case 'SNS Bank':
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "sns",
                name: "sns",
                viewBox: "0 0 25 25",
                width: 25,
                height: 25
            });
        default:
            return /*#__PURE__*/ React__default.createElement(Icon, {
                key: "bank",
                name: "bank",
                viewBox: "0 0 30 24",
                width: 30,
                height: 24
            });
    }
};

const cartPaymentMethods = ({ cartPaymentMethods: cartPaymentMethods1  }, isApplePayActive = false)=>{
    var ref;
    return (ref = cartPaymentMethods1 || []) === null || ref === void 0 ? void 0 : ref.reduce((acc, { name: name1 , psp , issuers , brands , installmentTypes  })=>{
        if (!isApplePayActive && name1 === PaymentMethod.APPLE_PAY) {
            return acc;
        }
        const icons = (brands === null || brands === void 0 ? void 0 : brands.length) && name1 !== PaymentMethod.APPLE_PAY ? getCreditCardBrandsIcons(brands) : getPaymentMethodIcon(name1);
        const paymentMethod = swcHelpers.objectSpread({
            name: name1,
            provider: psp
        }, (icons === null || icons === void 0 ? void 0 : icons.length) && {
            icons
        }, (issuers === null || issuers === void 0 ? void 0 : issuers.length) && {
            items: issuers.map(({ id , name  })=>({
                    key: id,
                    value: name,
                    icon: getBankIcon(name)
                })
            )
        }, (brands === null || brands === void 0 ? void 0 : brands.length) && {
            brands
        }, (installmentTypes === null || installmentTypes === void 0 ? void 0 : installmentTypes.length) && {
            items: installmentTypes.map(({ id , installments  })=>({
                    key: id,
                    value: String(installments)
                })
            )
        });
        return [
            ...acc,
            paymentMethod
        ];
    }, []);
};

const mapExpressPaymentMethods = (paymentMethods)=>{
    return (paymentMethods !== null && paymentMethods !== void 0 ? paymentMethods : []).map((paymentMethod)=>{
        return {
            name: paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.name,
            psp: paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.psp,
            brands: paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.brands
        };
    });
};

const errorsMapping = {
    SOMETHING_WENT_WRONG: 'errors.somethingWentWrong',
    ORDER_NOT_FOUND: 'checkout.errors.orderNotFound'
};

/**
 * Backend response from api-client is an enum value
 * The FE wants to use string constants for easier checks and handling
 *
 * This abstraction also makes it possible to migrate to grapqhl later on
 */ const mapPaymentState = (paymentState)=>{
    switch(paymentState.paymentState){
        case Orders.PaymentState.Pending:
            return 'Pending';
        case Orders.PaymentState.Paid:
            return 'Paid';
        case Orders.PaymentState.Failed:
            return 'Failed';
        case Orders.PaymentState.BalanceDue:
            return 'BalanceDue';
        case Orders.PaymentState.CreditOwed:
            return 'CreditOwed';
        default:
            return undefined;
    }
};

const GET_CART_PAYMENT_METHODS = gql`
  query CartPaymentMethods($cartId: ID!) {
    cartPaymentMethods(cartId: $cartId) {
      psp
      name
      issuers {
        id
        name
      }
      brands
      installmentTypes {
        id
        installments
      }
    }
  }
`;

const GET_CART_EXPRESS_PAYMENT_METHODS = gql`
  query CartExpressPaymentMethods($cartId: ID!) {
    cartExpressPaymentMethods(cartId: $cartId) {
      name
      psp
      brands
    }
  }
`;

const useIsApplePayActive = ()=>{
    const { isApplePayExpressCheckoutEnabled  } = useFeatureToggles();
    const isApplePayActive = React.useMemo(()=>{
        var ref;
        return isApplePayExpressCheckoutEnabled && typeof ApplePaySession !== 'undefined' && (ApplePaySession === null || ApplePaySession === void 0 ? void 0 : (ref = ApplePaySession.canMakePayments) === null || ref === void 0 ? void 0 : ref.call(ApplePaySession));
    }, [
        isApplePayExpressCheckoutEnabled
    ]);
    return isApplePayActive;
};

const useGetCartPaymentMethods = ()=>{
    const isApplePayActive = useIsApplePayActive();
    const [getCartPaymentMethods, { called , loading , data , error: error1  }] = useLazyQuery(GET_CART_PAYMENT_METHODS, {
        onError (error) {
            console.error(error);
            // General error for apollo error response
            toast.failed(errorsMapping.SOMETHING_WENT_WRONG);
        }
    });
    const getPaymentMethods = React.useCallback((cartId)=>{
        if (cartId) {
            getCartPaymentMethods({
                variables: {
                    cartId
                }
            });
        }
    }, [
        getCartPaymentMethods
    ]);
    const queryResults = React.useMemo(()=>({
            data: data ? cartPaymentMethods(data, isApplePayActive) : undefined,
            called,
            loading,
            error: error1
        })
    , [
        called,
        data,
        error1,
        loading,
        isApplePayActive
    ]);
    return [
        getPaymentMethods,
        queryResults
    ];
};

const useGetCartExpressPaymentMethods = ()=>{
    const [getCartExpressPaymentMethods, { called , loading , data , error: error1  }] = useLazyQuery(GET_CART_EXPRESS_PAYMENT_METHODS, {
        onError (error) {
            console.error(error);
            // General error for apollo error response
            toast.failed(errorsMapping.SOMETHING_WENT_WRONG);
        }
    });
    const getCartExpressPaymentMethodsMemoized = React.useCallback((cartId)=>{
        if (cartId) {
            getCartExpressPaymentMethods({
                variables: {
                    cartId
                }
            });
        }
    }, [
        getCartExpressPaymentMethods
    ]);
    return [
        getCartExpressPaymentMethodsMemoized,
        {
            data: mapExpressPaymentMethods(data === null || data === void 0 ? void 0 : data.cartExpressPaymentMethods),
            called,
            error: error1,
            loading
        }, 
    ];
};

const useIsApplePaySupportedForCart = ()=>{
    const isApplePayActive = useIsApplePayActive();
    const [getExpressPaymentMethods, { loading , called , data , error  }] = useGetCartExpressPaymentMethods();
    const getIsApplePaySupportedForCart = React__default.useCallback((cartId)=>{
        if (cartId) {
            getExpressPaymentMethods(cartId);
        }
    }, [
        getExpressPaymentMethods
    ]);
    if (!isApplePayActive) {
        return [
            ()=>undefined
            ,
            {
                isApplePaySupportedForCart: false,
                loading: false,
                called: true,
                error: undefined
            }, 
        ];
    }
    return [
        getIsApplePaySupportedForCart,
        {
            isApplePaySupportedForCart: !!(data === null || data === void 0 ? void 0 : data.find(({ name  })=>name === PaymentMethod.APPLE_PAY
            )),
            loading,
            called,
            error
        }, 
    ];
};

const usePaymentMethodLabelsMap = ()=>{
    const { getLabel  } = useIntl();
    return React__default.useMemo(()=>({
            [PaymentMethod.APPLE_PAY]: getLabel('checkout.payment.method.applepay'),
            [PaymentMethod.GOOGLE_PAY]: getLabel('checkout.payment.method.googlepay'),
            [PaymentMethod.BANCONTACT_CARD]: getLabel('checkout.payment.method.bancontactCard'),
            [PaymentMethod.BANCONTACT_MOBILE]: getLabel('checkout.payment.method.bancontactMobile'),
            [PaymentMethod.CASH_ON_DELIVERY]: getLabel('checkout.payment.method.cashOnDelivery'),
            [PaymentMethod.CREDITCARD]: getLabel('checkout.payment.method.creditcard'),
            [PaymentMethod.FAKE]: 'Fake payment',
            [PaymentMethod.IDEAL]: getLabel('checkout.payment.method.ideal'),
            [PaymentMethod.KLARNA_ACCOUNT]: getLabel('checkout.payment.method.klarna_account'),
            [PaymentMethod.KLARNA]: getLabel('checkout.payment.method.klarna'),
            [PaymentMethod.MBWAY]: getLabel('checkout.payment.method.mbway'),
            [PaymentMethod.MOBILE_PAY]: getLabel('checkout.payment.method.mobilepay'),
            [PaymentMethod.MULTIBANCO]: getLabel('checkout.payment.method.multibanco'),
            [PaymentMethod.ONEY]: getLabel('checkout.payment.method.oney'),
            [PaymentMethod.PAYPAL]: getLabel('checkout.payment.method.paypal'),
            [PaymentMethod.SEPA_DIRECT_DEBIT]: getLabel('checkout.payment.method.sepadirectdebit'),
            [PaymentMethod.SWISH]: getLabel('checkout.payment.method.swish')
        })
    , [
        getLabel
    ]);
};

const useGetAdditionalCartCost = ()=>{
    const apiConfig = useSelector(ApiConfigSelectors.getApiConfig);
    const [additionalCosts, setAdditionalCosts] = React.useState([]);
    const getAdditionalCosts = React.useCallback(()=>{
        if (!apiConfig) {
            throw new Error('Need API URL to get additional cart costs.');
        }
        Products.query(apiConfig === null || apiConfig === void 0 ? void 0 : apiConfig.productsUrl, {
            // Main category is called additionalCartCosts, but the "products" are configured in additionalPaymentCost
            categoryType: 'additionalPaymentCost'
        }).then(({ results  })=>{
            if (results) {
                setAdditionalCosts(results);
            } else {
                setAdditionalCosts([]);
            }
        }).catch((error)=>{
            if (error instanceof Error) {
                console.error(error);
            } else {
                console.error('Unable to load additional cart cost');
            }
        });
    }, [
        apiConfig
    ]);
    return {
        getAdditionalCosts,
        additionalCosts
    };
};

const AdyenContext = /*#__PURE__*/ React.createContext({
    clientKey: '',
    environment: 'test',
    AdyenModule: {
        result: undefined,
        loading: true
    }
});
const useAdyen = ()=>React.useContext(AdyenContext)
;
const AdyenProvider = ({ children , settings  })=>{
    // The `@adyen/adyen-web` package uses a hard reference to the global `window` variable. This makes it
    // impossible to import the package in a normal manner. Hence the choice to use dynamic importing.
    const [result, loading] = useDynamicModule(swcHelpers.asyncToGenerator(function*() {
        if (typeof window !== 'undefined') {
            return yield import('@adyen/adyen-web').then((module)=>module.default
            );
        }
        return undefined;
    }), '@adyen/adyen-web', []);
    const memoizedSettings = React.useMemo(()=>swcHelpers.objectSpread({}, settings, {
            AdyenModule: {
                result: result !== null && result !== void 0 ? result : undefined,
                loading
            }
        })
    , [
        settings,
        loading,
        result
    ]);
    return /*#__PURE__*/ React.createElement(AdyenContext.Provider, {
        value: memoizedSettings
    }, children);
};

const paymentsUIContextValues = {
    isProcessingOrder: false,
    setIsProcessingOrder: ()=>null
    ,
    paymentQRCodeState: undefined,
    setPaymentQRCodeState: ()=>null
    ,
    isQrCodeModalOpen: false,
    setIsQrCodeModalOpen: ()=>null
};
const PaymentsUIContext = /*#__PURE__*/ React__default.createContext(paymentsUIContextValues);
const usePaymentsUIContext = ()=>{
    const context = React__default.useContext(PaymentsUIContext);
    if (context === paymentsUIContextValues) {
        throw new Error('No PaymentUI Context available. You might still need to wrap the application with the paymentUIContextProvider component.');
    }
    return context;
};
PaymentsUIContext.displayName = 'PaymentsUIContext';
PaymentsUIContext.Consumer;

const PaymentsUIContextProvider = ({ children  })=>{
    const [paymentQRCodeState, setPaymentQRCodeState] = useState(undefined);
    const [isQrCodeModalOpen, setIsQrCodeModalOpen] = useState(false);
    const [isProcessingOrder, setIsProcessingOrder] = useState(false);
    const contextValue = React__default.useMemo(()=>({
            isProcessingOrder,
            setIsProcessingOrder,
            paymentQRCodeState,
            setPaymentQRCodeState,
            isQrCodeModalOpen,
            setIsQrCodeModalOpen
        })
    , [
        isProcessingOrder,
        isQrCodeModalOpen,
        paymentQRCodeState
    ]);
    return /*#__PURE__*/ React__default.createElement(PaymentsUIContext.Provider, {
        value: contextValue
    }, children);
};

/**
 * Continuously polls the backend for the payment state of the order
 * Polling will stop once an endstate is found. This can be either 'Paid' or 'Failed'
 *
 * There are two additional states called 'BalanceDue' and 'CreditOwed'. These are not used, but do require polling.
 *
 * The default polling interval is 5 seconds. This can be changed by setting the `pollInterval` option.
 */ const usePaymentState = (orderId, pollInterval = 5000)=>{
    const [state, setState] = useState(undefined);
    const [error1, setError] = useState();
    const routes = useApiConfig();
    const handler = useCallback(swcHelpers.asyncToGenerator(function*() {
        if (!orderId) {
            return;
        }
        try {
            const response = yield Orders.getPaymentState(routes.ordersUrl, {
                orderId
            });
            setState(mapPaymentState(response));
        } catch (error) {
            if (error instanceof ResponseError && error.statusCode === 404) {
                setError(errorsMapping.ORDER_NOT_FOUND);
            } else {
                setError(errorsMapping.SOMETHING_WENT_WRONG);
            }
        }
    }), [
        routes.ordersUrl,
        orderId
    ]);
    const options = useMemo(()=>({
            when: state !== 'Paid' && state !== 'Failed' && isNil(error1),
            startImmediate: true
        })
    , [
        state,
        error1
    ]);
    useInterval(handler, pollInterval, options);
    return [
        state,
        error1
    ];
};

const useReplicateCart = (refetch)=>{
    const apiConfig = useSelector(ApiConfigSelectors.getApiConfig);
    const replicateCart = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(orderId) {
            if (!apiConfig) {
                throw new Error('Need API configuration to replicate cart.');
            }
            return Carts.replicateCart(apiConfig.cartsUrl, {
                reference: {
                    typeId: 'order',
                    id: orderId
                }
            }).then((responseData)=>{
                refetch(responseData.id);
                return {
                    cartId: responseData.id
                };
            }).catch((error)=>{
                if (error instanceof Error) {
                    console.error(error);
                }
                throw new Error('Unknown error occurred while replicating cart, the current cart might be invalid');
            });
        });
        return function(orderId) {
            return _ref.apply(this, arguments);
        };
    }(), [
        apiConfig,
        refetch
    ]);
    return React.useMemo(()=>[
            replicateCart
        ]
    , [
        replicateCart
    ]);
};

const useQrPaymentState = ({ paymentState , orderId: orderId1 , error , paymentResponse , paymentFlow , orderToken , onQrCodeRefresh , refetch , cart ,  })=>{
    const [isQrCodeExpired, setIsQrCodeExpired] = useState(false);
    const [isQrRefreshLoading, setIsQrRefreshLoading] = useState(false);
    const [qrCode, setQrCode] = useState('');
    const [qrCodeExpireIn, setQrCodeExpireIn] = useState(0);
    const [externalUrl, setExternalUrl] = useState('');
    const [replicateCart] = useReplicateCart(refetch);
    const { setPaymentQRCodeState , isQrCodeModalOpen , setIsQrCodeModalOpen  } = usePaymentsUIContext();
    /*
   *  When user cancels payment or payment fails
   *  actions: 1. Replicate
   *           2. Call refetch
   *           4. Close QR modal
   */ const cancelPayment = useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(orderId) {
            const { cartId  } = yield replicateCart(orderId);
            refetch(cartId);
            setIsQrCodeModalOpen(false);
        });
        return function(orderId) {
            return _ref.apply(this, arguments);
        };
    }(), [
        refetch,
        replicateCart,
        setIsQrCodeModalOpen
    ]);
    /*
   *  Listen for QR code payment status
   *  if paymentState is updated set paymentQRCodeState in PaymentsUI Context
   *  if PaymentState is 'Failed' or any error occurred, replicate and restore cart
   */ useEffect(()=>{
        if (paymentState) {
            setPaymentQRCodeState(paymentState);
        }
        if (paymentState === 'Failed' || error) {
            cancelPayment(orderId1);
        }
    }, [
        paymentState,
        error,
        cancelPayment,
        orderId1,
        orderToken,
        setPaymentQRCodeState
    ]);
    /*
   *  Reset QR code timeout
   *  Refresh QR code image
   */ useEffect(()=>{
        if (qrCodeExpireIn) {
            const timeoutId = setTimeout(()=>{
                setIsQrCodeExpired(true);
                setQrCodeExpireIn(0);
            }, qrCodeExpireIn);
            return ()=>clearTimeout(timeoutId)
            ;
        }
    }, [
        qrCodeExpireIn
    ]);
    /*
   *  Set/Update properties when QR code is updated
   */ useEffect(()=>{
        var ref, ref1, ref2;
        setQrCodeExpireIn(Number(paymentResponse === null || paymentResponse === void 0 ? void 0 : (ref = paymentResponse.resultDetails) === null || ref === void 0 ? void 0 : ref.qrCodeExpiresIn) * 1000);
        setQrCode(String(paymentResponse === null || paymentResponse === void 0 ? void 0 : (ref1 = paymentResponse.resultDetails) === null || ref1 === void 0 ? void 0 : ref1.qrCodeUrl));
        setExternalUrl(String(paymentResponse === null || paymentResponse === void 0 ? void 0 : (ref2 = paymentResponse.resultDetails) === null || ref2 === void 0 ? void 0 : ref2.url));
        setIsQrCodeExpired(false);
        setIsQrRefreshLoading(false);
    }, [
        paymentResponse
    ]);
    // Open QR code modal only when PaymentFlow is QR_Code
    useEffect(()=>{
        if (paymentFlow === PaymentFlow.QR_CODE) {
            setIsQrCodeModalOpen(true);
        }
    }, [
        paymentFlow,
        setIsQrCodeModalOpen
    ]);
    /*
   *  On refreshQr replicate cart
   *  Get latest QR code
   */ const handleRefreshQrCode = useCallback(swcHelpers.asyncToGenerator(function*() {
        if (cart && orderId1) {
            setQrCodeExpireIn(0);
            setIsQrCodeExpired(true);
            yield replicateCart(orderId1);
            onQrCodeRefresh();
        }
        setIsQrRefreshLoading(true);
    }), [
        cart,
        onQrCodeRefresh,
        orderId1,
        replicateCart
    ]);
    return useMemo(()=>({
            externalUrl,
            isOpen: isQrCodeModalOpen,
            isQrCodeExpired,
            isQrRefreshLoading,
            onCancel: ()=>cancelPayment(orderId1)
            ,
            qrCodeUrl: qrCode,
            refreshQrCode: ()=>handleRefreshQrCode()
        })
    , [
        cancelPayment,
        externalUrl,
        handleRefreshQrCode,
        isQrCodeExpired,
        isQrCodeModalOpen,
        isQrRefreshLoading,
        orderId1,
        qrCode, 
    ]);
};

const ApplePayCaption = ({ type  })=>{
    const { getLabel  } = useIntl();
    let descriptionText;
    switch(type){
        case 'navigate-to-express-checkout':
            descriptionText = getLabel('checkout.express.apple.pay.action.continue');
            break;
        case 'express-checkout':
            descriptionText = getLabel('checkout.express.apple.pay.action.checkout');
            break;
        case 'regular-checkout':
            descriptionText = getLabel('checkout.express.apple.pay.action.payWith');
            break;
    }
    return /*#__PURE__*/ React.createElement("span", {
        className: "apple-pay-caption"
    }, descriptionText, /*#__PURE__*/ React.createElement(Icon, {
        color: "white",
        name: "apple-checkout-button-icon"
    }), getLabel('checkout.express.apple.pay.action.pay'));
};
const ApplePayButton = ({ disabled , type , onClick  })=>{
    const caption = /*#__PURE__*/ React.createElement(ApplePayCaption, {
        type: type
    });
    return /*#__PURE__*/ React.createElement(Button, {
        disabled: disabled,
        className: "cart-navigation__button apple-pay",
        "data-t": type,
        caption: caption,
        onClick: onClick
    });
};

const ApplePayExpressCheckoutButtonContainer = ({ cartId  })=>{
    const cartLink = useLink({
        route: 'cart.overview'
    });
    const expressCheckoutLink = useLink({
        route: 'express-checkout',
        params: {
            type: ExpressPaymentMethod.APPLE_PAY,
            returnPath: cartLink === null || cartLink === void 0 ? void 0 : cartLink.href,
            cartId
        }
    });
    const [getIsApplePaySupportedForCart, { isApplePaySupportedForCart , called , loading  }] = useIsApplePaySupportedForCart();
    React.useEffect(()=>{
        if (cartId) {
            getIsApplePaySupportedForCart(cartId);
        }
    }, [
        cartId,
        getIsApplePaySupportedForCart
    ]);
    if (!isApplePaySupportedForCart && called && !loading) {
        return null;
    }
    if (!expressCheckoutLink) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(ApplePayButton, {
        type: "navigate-to-express-checkout",
        onClick: expressCheckoutLink.onClick
    });
};

const PaypalCaption = ()=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement("span", {
        className: "pay-pal-caption"
    }, getLabel('checkout.express.apple.pay.action.payWith'), /*#__PURE__*/ React.createElement(Icon, {
        name: "paypal-checkout-button-icon",
        viewBox: "0 0 77 22",
        width: 77,
        height: 22
    }));
};
const PaypalButton = ({ onClick , disabled =false  })=>{
    const caption = /*#__PURE__*/ React.createElement(PaypalCaption, null);
    const breakPoint = MediaQuery.useBreakpoint();
    return /*#__PURE__*/ React.createElement(Button, {
        disabled: disabled,
        size: breakPoint === MediaQuery.BreakpointName.TABLET_LANDSCAPE ? 'small' : undefined,
        className: "cart-navigation__button pay-pal",
        "data-t": "paypal-pay-action",
        caption: caption,
        onClick: onClick
    });
};
const PaypalActionButton = (props)=>{
    return /*#__PURE__*/ React.createElement(PaypalButton, swcHelpers.extends({}, props));
};

const PaymentForm = ({ paymentMethod , form , onHandlePaymentAction  })=>{
    const { getLabel , hasLabel  } = useIntl();
    const paymentMethodLabelsMap = usePaymentMethodLabelsMap();
    const breakPoint = MediaQuery.useBreakpoint();
    const { isProcessingOrder  } = usePaymentsUIContext();
    const [enableWaitText, setEnableWaitText] = React.useState(false);
    React.useEffect(()=>{
        if (isProcessingOrder) {
            const timeoutId = setTimeout(()=>{
                setEnableWaitText(true);
            }, 3000);
            return ()=>clearTimeout(timeoutId)
            ;
        } else {
            setEnableWaitText(false);
        }
    }, [
        isProcessingOrder
    ]);
    const actionButton = React.useMemo(()=>{
        switch(paymentMethod){
            case PaymentMethod.APPLE_PAY:
                return /*#__PURE__*/ React.createElement(ApplePayButton, {
                    type: "regular-checkout",
                    onClick: onHandlePaymentAction,
                    disabled: isProcessingOrder
                });
            case PaymentMethod.PAYPAL:
                return /*#__PURE__*/ React.createElement(PaypalActionButton, {
                    onClick: onHandlePaymentAction,
                    disabled: isProcessingOrder
                });
            default:
                return /*#__PURE__*/ React.createElement(Button, {
                    color: "primary",
                    size: breakPoint === MediaQuery.BreakpointName.TABLET_LANDSCAPE ? 'small' : undefined,
                    caption: `${getLabel(`checkout.action.pay.continueWith`)} ${paymentMethodLabelsMap[paymentMethod]}`,
                    "data-t": `button-next-payment`,
                    disabled: isProcessingOrder,
                    onClick: onHandlePaymentAction,
                    isLoading: isProcessingOrder
                });
        }
    }, [
        breakPoint,
        getLabel,
        isProcessingOrder,
        onHandlePaymentAction,
        paymentMethod,
        paymentMethodLabelsMap, 
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: "payment-form-container"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "payment-form-container__form"
    }, form && form), actionButton, hasLabel(`checkout.${paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.toLowerCase()}.instruction`) && /*#__PURE__*/ React.createElement(Text, {
        className: "payment-form-container__instruction"
    }, getLabel(`checkout.${paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.toLowerCase()}.instruction`)), enableWaitText && /*#__PURE__*/ React.createElement(Text, {
        className: "payment-form-container__instruction"
    }, getLabel(`checkout.payment.wait.instruction`)));
};

const getPaymentSelectOptions = (config)=>{
    if (config && 'items' in config && Array.isArray(config.items)) {
        return config.items.reduce((items, item)=>{
            if (item && typeof item.key === 'string' && typeof item.value === 'string') {
                if (item && item.icon) {
                    return swcHelpers.objectSpread({}, items, {
                        [item.value]: {
                            label: item.value,
                            value: item.key,
                            icon: item.icon
                        }
                    });
                }
                return swcHelpers.objectSpread({}, items, {
                    [item.value]: {
                        label: item.value,
                        value: item.key
                    }
                });
            }
            return items;
        }, {});
    }
    return {};
};

/**
 * The getRestPaymentMethodName function is being used to CONVERT the name of
 * the `payment method`, that is fetched by the getCartPaymentMethods query
 * TO the `payment method` name of the legacy api-client.
 *
 * @param {PaymentMethod} name - The name of the payment method.
 */ const getRestPaymentMethodName = (name)=>{
    switch(name){
        case PaymentMethod.BANCONTACT_CARD:
            return PaymentTypes.PaymentMethod.BancontactCard;
        case PaymentMethod.BANCONTACT_MOBILE:
            return PaymentTypes.PaymentMethod.BancontactMobile;
        case PaymentMethod.CASH_ON_DELIVERY:
            return PaymentTypes.PaymentMethod.CashOnDelivery;
        case PaymentMethod.CREDITCARD:
            return PaymentTypes.PaymentMethod.Creditcard;
        case PaymentMethod.FAKE:
            return PaymentTypes.PaymentMethod.Fake;
        case PaymentMethod.IDEAL:
            return PaymentTypes.PaymentMethod.Ideal;
        case PaymentMethod.KLARNA:
            return PaymentTypes.PaymentMethod.Klarna;
        case PaymentMethod.KLARNA_ACCOUNT:
            return PaymentTypes.PaymentMethod.KlarnaAccount;
        case PaymentMethod.MBWAY:
            return PaymentTypes.PaymentMethod.MBway;
        case PaymentMethod.MOBILE_PAY:
            return PaymentTypes.PaymentMethod.MobilePay;
        case PaymentMethod.MULTIBANCO:
            return PaymentTypes.PaymentMethod.Multibanco;
        case PaymentMethod.ONEY:
            return PaymentTypes.PaymentMethod.Oney;
        case PaymentMethod.PAYPAL:
            return PaymentTypes.PaymentMethod.Paypal;
        case PaymentMethod.SEPA_DIRECT_DEBIT:
            return PaymentTypes.PaymentMethod.SepaDirectDebit;
        case PaymentMethod.SWISH:
            return PaymentTypes.PaymentMethod.Swish;
        case PaymentMethod.APPLE_PAY:
            return PaymentTypes.PaymentMethod.ApplePay;
        default:
            return undefined;
    }
};

function useCheckout(onChange) {
    const { locale  } = useIntl();
    // Import the adyen web module
    const { AdyenModule , clientKey , environment  } = useAdyen();
    const options = React.useMemo(()=>({
            clientKey,
            environment,
            locale: `${locale}`,
            onChange
        })
    , [
        clientKey,
        environment,
        locale,
        onChange
    ]);
    // The checkout object is local to the current page, it needs to be memoized
    // so to handle the dynamic module loading correctly
    const checkout = React.useMemo(()=>{
        const AdyenCheckout = AdyenModule === null || AdyenModule === void 0 ? void 0 : AdyenModule.result;
        return AdyenCheckout && AdyenCheckout(options);
    }, [
        AdyenModule,
        options
    ]);
    return checkout;
}

const PaymentMethodFormContainer = ({ children , type , additionalOptions , checkout ,  })=>{
    const ref = React.useRef(null);
    // In order to properly unmount any mounted elements, the promise of the mounted element is used to
    // properly unmount it again whenever the cleanup cycle is triggered.
    React.useEffect(()=>{
        if (!checkout) {
            return;
        }
        const mountPayment = function() {
            var _ref = swcHelpers.asyncToGenerator(function*() {
                const c = yield checkout;
                const paymentMethod = c === null || c === void 0 ? void 0 : c.create(type, additionalOptions);
                var _current;
                return paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.mount((_current = ref.current) !== null && _current !== void 0 ? _current : '');
            });
            return function mountPayment() {
                return _ref.apply(this, arguments);
            };
        }();
        const promise = mountPayment();
        return ()=>{
            promise.then((component)=>{
                return component === null || component === void 0 ? void 0 : component.unmount();
            });
        };
    }, [
        type,
        additionalOptions,
        checkout
    ]);
    if (!checkout) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        ref: ref,
        className: "adyen-payment-method-form"
    }, children);
};

var ref, ref1, ref2, ref3, ref4;
const toPaymentMethodFormState$1 = (state, cardBrand)=>{
    var ref18, ref5, ref6, ref7, ref8, ref9, ref10, ref11, ref12, ref13, ref14, ref15, ref16, ref17;
    return {
        cardBrand,
        isValid: state.isValid,
        data: {
            browserInfo: {
                acceptHeader: state === null || state === void 0 ? void 0 : (ref18 = state.data) === null || ref18 === void 0 ? void 0 : (ref5 = ref18.browserInfo) === null || ref5 === void 0 ? void 0 : ref5.acceptHeader,
                userAgent: state === null || state === void 0 ? void 0 : (ref6 = state.data) === null || ref6 === void 0 ? void 0 : (ref7 = ref6.browserInfo) === null || ref7 === void 0 ? void 0 : ref7.userAgent
            },
            holderName: (ref = state === null || state === void 0 ? void 0 : (ref8 = state.data) === null || ref8 === void 0 ? void 0 : (ref9 = ref8.paymentMethod) === null || ref9 === void 0 ? void 0 : ref9.holderName) !== null && ref !== void 0 ? ref : '',
            encryptedCardNumber: (ref1 = state === null || state === void 0 ? void 0 : (ref10 = state.data) === null || ref10 === void 0 ? void 0 : (ref11 = ref10.paymentMethod) === null || ref11 === void 0 ? void 0 : ref11.encryptedCardNumber) !== null && ref1 !== void 0 ? ref1 : '',
            encryptedExpiryMonth: (ref2 = state === null || state === void 0 ? void 0 : (ref12 = state.data) === null || ref12 === void 0 ? void 0 : (ref13 = ref12.paymentMethod) === null || ref13 === void 0 ? void 0 : ref13.encryptedExpiryMonth) !== null && ref2 !== void 0 ? ref2 : '',
            encryptedExpiryYear: (ref3 = state === null || state === void 0 ? void 0 : (ref14 = state.data) === null || ref14 === void 0 ? void 0 : (ref15 = ref14.paymentMethod) === null || ref15 === void 0 ? void 0 : ref15.encryptedExpiryYear) !== null && ref3 !== void 0 ? ref3 : '',
            encryptedSecurityCode: (ref4 = state === null || state === void 0 ? void 0 : (ref16 = state.data) === null || ref16 === void 0 ? void 0 : (ref17 = ref16.paymentMethod) === null || ref17 === void 0 ? void 0 : ref17.encryptedSecurityCode) !== null && ref4 !== void 0 ? ref4 : ''
        }
    };
};
const CreditCardContainer = ({ onChange , additionalOptions , type =AdyenPaymentType.Card ,  })=>{
    const [state, setState] = React.useState();
    const [brand, setBrand] = React.useState();
    const checkout = useCheckout(setState);
    const additional = React.useMemo(()=>{
        return swcHelpers.objectSpread({
            hasHolderName: true,
            holderNameRequired: true
        }, additionalOptions, {
            onBrand: (obj)=>{
                var ref19;
                additionalOptions === null || additionalOptions === void 0 ? void 0 : (ref19 = additionalOptions.onBrand) === null || ref19 === void 0 ? void 0 : ref19.call(additionalOptions, obj);
                setBrand(obj.brand);
            }
        });
    }, [
        additionalOptions,
        setBrand
    ]);
    React.useEffect(()=>{
        if (state) {
            onChange(toPaymentMethodFormState$1(state, brand));
        }
    }, [
        brand,
        state,
        onChange
    ]);
    return /*#__PURE__*/ React.createElement(PaymentMethodFormContainer, {
        type: type,
        checkout: checkout,
        additionalOptions: additional
    });
};

const BankDropdown = ({ id , placeholder , options , onChange , name , label , required =true , hideAsteriskSign =false  })=>{
    const [showMenu, setShowMenu] = useState(false);
    const [selectedValue, setSelectedValue] = useState(null);
    const inputRef = useRef(null);
    useEffect(()=>{
        const handler = (e)=>{
            if (inputRef.current && !inputRef.current.contains(e.target)) {
                setShowMenu(false);
            }
        };
        window.addEventListener('click', handler);
        return ()=>{
            window.removeEventListener('click', handler);
        };
    });
    const handleInputClick = ()=>{
        setShowMenu(!showMenu);
    };
    const handleOnInputKeyDown = (e)=>{
        if ([
            'Enter',
            ' '
        ].includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();
            handleInputClick();
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setShowMenu(false);
        }
    };
    const onItemClick = (option)=>{
        setSelectedValue(option);
        onChange && onChange({
            label: option.label,
            value: option.value
        });
        setShowMenu(false);
    };
    const handleOnItemKeyDown = (e, option)=>{
        if ([
            'Enter',
            ' '
        ].includes(e.key)) {
            var ref;
            e.preventDefault();
            e.stopPropagation();
            onItemClick(option);
            (ref = inputRef.current) === null || ref === void 0 ? void 0 : ref.focus();
        }
        if (e.key === 'Escape') {
            var ref1;
            e.preventDefault();
            e.stopPropagation();
            setShowMenu(false);
            (ref1 = inputRef.current) === null || ref1 === void 0 ? void 0 : ref1.focus();
        }
    };
    const isSelected = (option)=>{
        if (!selectedValue) {
            return false;
        }
        return selectedValue.value === option.value;
    };
    const getOptions = ()=>{
        return Object.values(options);
    };
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "dropdown-field"
    }, /*#__PURE__*/ React__default.createElement("label", {
        className: "dropdown-field__label",
        htmlFor: id,
        id: "dropdown-label"
    }, label, required && !hideAsteriskSign && /*#__PURE__*/ React__default.createElement("span", {
        className: "dropdown-field__asterisk"
    }, "*")), /*#__PURE__*/ React__default.createElement("div", {
        className: "dropdown-field__container"
    }, /*#__PURE__*/ React__default.createElement("div", {
        id: id,
        ref: inputRef,
        onClick: handleInputClick,
        onKeyDown: handleOnInputKeyDown,
        className: "dropdown-field__input",
        role: "combobox",
        "aria-controls": name,
        "aria-expanded": showMenu,
        "aria-labelledby": "dropdown-label",
        tabIndex: 0
    }, !selectedValue ? /*#__PURE__*/ React__default.createElement("div", {
        className: "dropdown-field__placeholder"
    }, placeholder) : /*#__PURE__*/ React__default.createElement("div", {
        className: "dropdown-field__value"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "dropdown-field__bank-icon"
    }, selectedValue.icon), selectedValue.label)), /*#__PURE__*/ React__default.createElement(Icon, {
        name: "chevron-down",
        className: "select-field__icon"
    })), showMenu && /*#__PURE__*/ React__default.createElement("div", {
        className: "dropdown-field__menu"
    }, getOptions().map((option)=>/*#__PURE__*/ React__default.createElement("div", {
            onClick: ()=>onItemClick(option)
            ,
            key: option.value,
            className: `dropdown-item ${isSelected(option) && 'selected'}`,
            onKeyDown: (event)=>handleOnItemKeyDown(event, option)
            ,
            role: "option",
            "aria-selected": isSelected(option),
            tabIndex: 0
        }, /*#__PURE__*/ React__default.createElement("div", {
            className: "dropdown-field__bank-icon"
        }, option.icon), option.label)
    )));
};

const IdealContainer = ({ items , onChange  })=>{
    const { getLabel  } = useIntl();
    const { isPaymentMethodsNewUIEnabled  } = useFeatureToggles();
    const labels = React.useMemo(()=>({
            title: getLabel('checkout.payment.method.ideal.select.title'),
            info: getLabel('checkout.payment.method.ideal.info')
        })
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(BankDropdown, {
        id: "idealBankSelect",
        name: "idealBankSelect",
        placeholder: labels.title,
        options: items,
        onChange: onChange,
        label: labels.title
    }), isPaymentMethodsNewUIEnabled && /*#__PURE__*/ React.createElement(Text, {
        className: "ideal-payment__info"
    }, labels.info));
};

const Mbway$1 = ({ initialValue , labels , onChange , validations  })=>{
    const { isPaymentMethodsNewUIEnabled  } = useFeatureToggles();
    return /*#__PURE__*/ React.createElement("div", {
        className: "mbway-payment checkout-payment-wrapper"
    }, /*#__PURE__*/ React.createElement(React.Fragment, null, isPaymentMethodsNewUIEnabled ? /*#__PURE__*/ React.createElement(Text, {
        className: "mbway-payment__info"
    }, labels.info) : /*#__PURE__*/ React.createElement(Alert, {
        className: "mbway-payment__alert",
        title: labels.hintTitle,
        message: labels.hintMessage
    }), /*#__PURE__*/ React.createElement(PhoneNumber, {
        initialState: {
            value: initialValue,
            isTouched: false
        },
        required: true,
        onChange: onChange,
        validations: validations,
        label: labels.phoneNumberLabel,
        placeholder: labels.phoneNumberPlaceholder,
        errorMessage: labels.phoneNumberError
    })));
};

const isValidPortugueseMobilePhoneNumber = (value)=>/^(\+351|00351|351)([9]{1})(6|3|2|1{1})([0-9]{7})$/.test(unspaceString(value))
;

const MbwayContainer = ({ initialValue , onChange  })=>{
    const { getLabel  } = useIntl();
    const handleOnChange = React.useCallback((state)=>{
        onChange({
            telephoneNumber: unspaceString(state.value)
        }, isValidPortugueseMobilePhoneNumber(state.value));
    }, [
        onChange
    ]);
    const labels = React.useMemo(()=>{
        return {
            hintTitle: getLabel('checkout.payment.method.mbWay.hint.title'),
            hintMessage: getLabel('checkout.payment.method.mbWay.hint.message'),
            phoneNumberLabel: getLabel('checkout.contact.phoneNumber'),
            phoneNumberPlaceholder: getLabel('checkout.payment.method.mbWay.phoneNumber.placeholder'),
            phoneNumberError: getLabel('checkout.contact.phoneNumber.errorMessage'),
            info: getLabel('checkout.payment.method.mbWay.info')
        };
    }, [
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(Mbway$1, {
        initialValue: initialValue,
        labels: labels,
        onChange: handleOnChange,
        validations: [
            isValidPortugueseMobilePhoneNumber
        ]
    });
};

const Oney$1 = ({ options , labels , onChange  })=>/*#__PURE__*/ React.createElement(Select, {
        id: "oneyInstallments",
        name: "oneyInstallments",
        options: options,
        onChange: onChange,
        label: labels.title,
        placeholder: labels.placeholder,
        required: true
    })
;

const OneyContainer = ({ onChange , options  })=>{
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            title: getLabel('checkout.payment.method.oney.select.title'),
            placeholder: getLabel('checkout.payment.method.oney.select.placeholder')
        })
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(Oney$1, {
        labels: labels,
        onChange: onChange,
        options: options
    });
};

// This component will create any Adyen web component that can be created from it's "action" prop.
//
// NOTE:
// currently only tested with the "redirect" action
const ActionContainer = ({ action  })=>{
    const ref = React.useRef(null);
    const checkout = useCheckout();
    // Mounts the action component if we receive an action from adyen
    React.useEffect(()=>{
        if (!checkout || !action) {
            return;
        }
        const mountAction = function() {
            var _ref = swcHelpers.asyncToGenerator(function*() {
                const c = yield checkout;
                const actionComponent = c === null || c === void 0 ? void 0 : c.createFromAction(action);
                var _current;
                return actionComponent === null || actionComponent === void 0 ? void 0 : actionComponent.mount((_current = ref.current) !== null && _current !== void 0 ? _current : '');
            });
            return function mountAction() {
                return _ref.apply(this, arguments);
            };
        }();
        const promise = mountAction();
        return ()=>{
            promise.then((component)=>{
                return component === null || component === void 0 ? void 0 : component.unmount();
            });
        };
    }, [
        checkout,
        action
    ]);
    if (!action) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        ref: ref,
        className: "adyen-action-component"
    });
};

var _ownerName, _iban;
const toPaymentMethodFormState = (state)=>({
        isValid: state.isValid,
        data: {
            'sepa.ownerName': (_ownerName = state.data.paymentMethod.ownerName) !== null && _ownerName !== void 0 ? _ownerName : '',
            'sepa.ibanNumber': (_iban = state.data.paymentMethod.iban) !== null && _iban !== void 0 ? _iban : ''
        }
    })
;
const SepaContainer = ({ onChange  })=>{
    const setSepaState = React.useCallback((state)=>onChange(toPaymentMethodFormState(state))
    , [
        onChange
    ]);
    const checkout = useCheckout(setSepaState);
    return /*#__PURE__*/ React.createElement(PaymentMethodFormContainer, {
        type: AdyenPaymentType.Sepa,
        checkout: checkout
    });
};

const Bancontact = ({ onChange , config: paymentConfig  })=>{
    const { config  } = useAdyen();
    const bancontactConfig = React.useMemo(()=>{
        const configData = config === null || config === void 0 ? void 0 : config.bancontactCard;
        if (configData && (paymentConfig === null || paymentConfig === void 0 ? void 0 : paymentConfig.brands)) {
            configData.brands = paymentConfig === null || paymentConfig === void 0 ? void 0 : paymentConfig.brands;
        }
        return configData;
    }, [
        config,
        paymentConfig
    ]);
    const handleChange = React.useCallback((state)=>{
        let paymentMethodType;
        if (state.cardBrand === AdyenBrands.VISA || state.cardBrand === AdyenBrands.MAESTRO) {
            paymentMethodType = PaymentTypes.PaymentMethod.Creditcard;
        }
        onChange(state.data, state.isValid, paymentMethodType);
    }, [
        onChange
    ]);
    return /*#__PURE__*/ React.createElement(CreditCardContainer, {
        onChange: handleChange,
        additionalOptions: bancontactConfig,
        type: AdyenPaymentType.Bancontact
    });
};

const AdyenPaymentCardSummary = ({ info , intl  })=>{
    if (!info || !('creditcardBin' in info) || typeof info.creditcardBin !== 'string') {
        return null;
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "checkout-payment-info__label"
    }, intl.getLabel('checkout.creditcard.cardNumber')), /*#__PURE__*/ React.createElement("div", {
        className: "checkout-payment-info__value"
    }, info.creditcardBin.substr(0, 4), " **** **** ****"));
};
var CardSummary = withIntl(AdyenPaymentCardSummary);

const BancontactSummary = (props)=>/*#__PURE__*/ React.createElement(CardSummary, swcHelpers.extends({}, props))
;

const Creditcard = ({ onChange , config: paymentConfig  })=>{
    const { config  } = useAdyen();
    const creditcardConfig = React.useMemo(()=>{
        const configData = config === null || config === void 0 ? void 0 : config.creditcard;
        if (configData && (paymentConfig === null || paymentConfig === void 0 ? void 0 : paymentConfig.brands)) {
            configData.brands = paymentConfig === null || paymentConfig === void 0 ? void 0 : paymentConfig.brands;
        }
        return configData;
    }, [
        config,
        paymentConfig
    ]);
    const handleChange = React.useCallback((state)=>{
        let paymentMethodType;
        if (state.cardBrand === AdyenBrands.BANCONTACTMC) {
            paymentMethodType = PaymentTypes.PaymentMethod.BancontactCard;
        }
        // Once Cartes Bancaires is added/requested add transformation here
        onChange(state.data, state.isValid, paymentMethodType);
    }, [
        onChange
    ]);
    return /*#__PURE__*/ React.createElement(CreditCardContainer, {
        onChange: handleChange,
        additionalOptions: creditcardConfig
    });
};

const CreditcardSummary = (props)=>/*#__PURE__*/ React.createElement(CardSummary, swcHelpers.extends({}, props))
;

const PaymentInstructionLayout = ({ info  })=>{
    const { getLabel  } = useIntl();
    const { isPaymentMethodsNewUIEnabled  } = useFeatureToggles();
    return isPaymentMethodsNewUIEnabled ? /*#__PURE__*/ React.createElement(Text, null, getLabel(info)) : null;
};

const ApplePay = ()=>{
    return /*#__PURE__*/ React.createElement(PaymentInstructionLayout, {
        info: "checkout.payment.method.applepay.info"
    });
};

const BancontactMobile = ()=>{
    return /*#__PURE__*/ React.createElement(PaymentInstructionLayout, {
        info: "checkout.payment.method.bancontact_mobile.info"
    });
};

const CashOnDelivery = ()=>{
    return /*#__PURE__*/ React.createElement(PaymentInstructionLayout, {
        info: "checkout.payment.method.cash_on_delivery.info"
    });
};

const Ideal = ({ config , onChange  })=>{
    const selectItems = React.useMemo(()=>getPaymentSelectOptions(config)
    , [
        config
    ]);
    const onHandleChange = React.useCallback((selectedBank)=>{
        if (selectedBank.label && selectedBank.value) {
            onChange({
                issuer: selectedBank.value,
                issuerName: selectedBank.label
            }, true);
        } else {
            onChange({}, false);
        }
    }, [
        onChange
    ]);
    return /*#__PURE__*/ React.createElement(IdealContainer, {
        items: selectItems,
        onChange: onHandleChange
    });
};

const Klarna = ()=>{
    return /*#__PURE__*/ React.createElement(PaymentInstructionLayout, {
        info: "checkout.payment.method.klarna.info"
    });
};

const KlarnaAccount = ()=>{
    return /*#__PURE__*/ React.createElement(PaymentInstructionLayout, {
        info: "checkout.payment.method.klarna_account.info"
    });
};

const isMbwayDetails = (details)=>{
    if (!details || typeof details !== 'object') {
        return false;
    }
    return 'telephoneNumber' in details && !!details.telephoneNumber;
};

const Mbway = ({ details , onChange  })=>{
    const initialValue = isMbwayDetails(details) ? details.telephoneNumber : '+351';
    return /*#__PURE__*/ React.createElement(MbwayContainer, {
        initialValue: initialValue,
        onChange: onChange
    });
};

const MbwaySummary = ({ details  })=>{
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            phoneNumber: getLabel('checkout.contact.phoneNumber'),
            hintTitle: getLabel('checkout.completed.paymentDetails.mbway.hint.title'),
            hintMessage: getLabel('checkout.completed.paymentDetails.mbway.hint.message')
        })
    , [
        getLabel
    ]);
    if (!isMbwayDetails(details)) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "checkout-payment-info__label"
    }, labels.phoneNumber), /*#__PURE__*/ React.createElement("div", {
        className: "checkout-payment-info__value"
    }, details.telephoneNumber), /*#__PURE__*/ React.createElement(Alert, {
        className: "checkout-payment-info__alert",
        title: labels.hintTitle,
        message: labels.hintMessage
    }));
};

const MobilePay = ()=>{
    return /*#__PURE__*/ React.createElement(PaymentInstructionLayout, {
        info: "checkout.payment.method.mobile_pay.info"
    });
};

const MultibancoSummary = ({ labels , paymentExternalId , paymentExternalReference  })=>/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "checkout-payment-info__label"
    }, labels.multibancoEntity), /*#__PURE__*/ React.createElement("div", {
        className: "checkout-payment-info__value"
    }, paymentExternalId), /*#__PURE__*/ React.createElement("div", {
        className: "checkout-payment-info__label"
    }, labels.multibancoReference), /*#__PURE__*/ React.createElement("div", {
        className: "checkout-payment-info__value"
    }, paymentExternalReference), /*#__PURE__*/ React.createElement(Alert, {
        className: "checkout-payment-info__alert",
        title: labels.multibancoTitle,
        message: labels.multibancoMessage
    }))
;

const isMultibancoResult = (result)=>{
    if (!result || typeof result !== 'object') {
        return false;
    }
    return 'paymentExternalReference' in result && typeof result.paymentExternalReference === 'string' && 'paymentExternalId' in result && typeof result.paymentExternalId === 'string';
};
const Multibanco$1 = ({ result  })=>{
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            multibancoEntity: getLabel('checkout.completed.paymentDetails.multibanco.entity'),
            multibancoReference: getLabel('checkout.completed.paymentDetails.multibanco.reference'),
            multibancoMessage: getLabel('checkout.completed.paymentDetails.multibanco.hint.message'),
            multibancoTitle: getLabel('checkout.completed.paymentDetails.multibanco.hint.title')
        })
    , [
        getLabel
    ]);
    return isMultibancoResult(result) ? /*#__PURE__*/ React.createElement(MultibancoSummary, {
        labels: labels,
        paymentExternalId: result.paymentExternalId,
        paymentExternalReference: result.paymentExternalReference
    }) : null;
};

const Multibanco = ()=>{
    return /*#__PURE__*/ React.createElement(PaymentInstructionLayout, {
        info: "checkout.payment.method.multibanco.info"
    });
};

const Oney = ({ config , onChange  })=>{
    const oneyOptions = React.useMemo(()=>getPaymentSelectOptions(config)
    , [
        config
    ]);
    const onHandleChange = React.useCallback((selectedInstallment)=>{
        if (selectedInstallment.label && selectedInstallment.value) {
            onChange({
                type: selectedInstallment.value
            }, true);
        } else {
            onChange({}, false);
        }
    }, [
        onChange
    ]);
    return /*#__PURE__*/ React.createElement(OneyContainer, {
        options: oneyOptions,
        onChange: onHandleChange
    });
};

const Paypal = ()=>{
    return /*#__PURE__*/ React.createElement(PaymentInstructionLayout, {
        info: "checkout.payment.method.paypal.info"
    });
};

const Sepa = ({ onChange  })=>{
    const handleChange = React.useCallback((state)=>{
        onChange(state.data, state.isValid);
    }, [
        onChange
    ]);
    return /*#__PURE__*/ React.createElement(SepaContainer, {
        onChange: handleChange
    });
};

const SepaSummary = ({ info  })=>{
    const { getLabel  } = useIntl();
    if (!info || !('iban' in info) || typeof info.iban !== 'string') {
        return null;
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "checkout-payment-info__label"
    }, getLabel('ibanNumber')), /*#__PURE__*/ React.createElement("div", {
        className: "checkout-payment-info__value"
    }, info.iban));
};

const Swish = ()=>{
    return /*#__PURE__*/ React.createElement(PaymentInstructionLayout, {
        info: "checkout.payment.method.swish.info"
    });
};

const Fake = ({ details , label , onChange  })=>{
    const initialState = details && typeof details.alwaysFail === 'boolean' ? details.alwaysFail : false;
    const [isChecked, setIsChecked] = React.useState(initialState);
    const convertStateToPaymentDetails = (state)=>({
            alwaysFail: state,
            chanceToFailRegistration: false
        })
    ;
    const handleOnChange = ({ checked  })=>{
        setIsChecked(checked);
        onChange(convertStateToPaymentDetails(checked), true);
    };
    React.useEffect(()=>{
        if (!details) {
            onChange(convertStateToPaymentDetails(isChecked), true);
        }
    }, [
        details,
        isChecked,
        onChange
    ]);
    return /*#__PURE__*/ React.createElement(CheckboxWithLabel, {
        className: "fake-payment ",
        id: "fakePayment",
        value: "fail",
        checked: isChecked,
        onChange: handleOnChange,
        label: label || '🧨 Fail to register payment'
    });
};

const P = PaymentServiceProvider;
const M = PaymentMethod;
const providers = {
    [P.FAKE]: {
        [M.FAKE]: {
            type: M.FAKE,
            provider: P.FAKE,
            form: Fake
        }
    },
    [P.ADYEN]: {
        [M.IDEAL]: {
            type: M.IDEAL,
            provider: P.ADYEN,
            form: Ideal
        },
        [M.BANCONTACT_CARD]: {
            type: M.BANCONTACT_CARD,
            provider: P.ADYEN,
            form: Bancontact,
            summary: BancontactSummary
        },
        [M.CREDITCARD]: {
            type: M.CREDITCARD,
            provider: P.ADYEN,
            form: Creditcard,
            summary: CreditcardSummary
        },
        [M.PAYPAL]: {
            type: M.PAYPAL,
            provider: P.ADYEN,
            form: Paypal
        },
        [M.APPLE_PAY]: {
            type: M.APPLE_PAY,
            provider: P.ADYEN,
            form: ApplePay
        },
        [M.MOBILE_PAY]: {
            type: M.MOBILE_PAY,
            provider: P.ADYEN,
            form: MobilePay
        },
        [M.KLARNA]: {
            type: M.KLARNA,
            provider: P.ADYEN,
            form: Klarna,
            addCartToPayment: true
        },
        [M.KLARNA_ACCOUNT]: {
            type: M.KLARNA_ACCOUNT,
            provider: P.ADYEN,
            form: KlarnaAccount,
            addCartToPayment: true
        },
        [M.MULTIBANCO]: {
            type: M.MULTIBANCO,
            provider: P.ADYEN,
            form: Multibanco,
            summary: Multibanco$1
        },
        [M.MBWAY]: {
            type: M.MBWAY,
            provider: P.ADYEN,
            form: Mbway,
            summary: MbwaySummary
        },
        [M.SEPA_DIRECT_DEBIT]: {
            type: M.SEPA_DIRECT_DEBIT,
            provider: P.ADYEN,
            form: Sepa,
            summary: SepaSummary
        },
        [M.ONEY]: {
            type: M.ONEY,
            provider: P.ADYEN,
            form: Oney
        },
        [M.SWISH]: {
            type: M.SWISH,
            provider: P.ADYEN,
            form: Swish
        },
        [M.BANCONTACT_MOBILE]: {
            type: M.BANCONTACT_MOBILE,
            provider: P.ADYEN,
            form: BancontactMobile
        }
    },
    [P.CASH_ON_DELIVERY]: {
        [M.CASH_ON_DELIVERY]: {
            type: M.CASH_ON_DELIVERY,
            provider: P.CASH_ON_DELIVERY,
            form: CashOnDelivery,
            addCartToPayment: true
        }
    }
};
const getPaymentImplementation = (provider, paymentMethod)=>{
    const providerMap = providers[provider];
    if (!providerMap) {
        return undefined;
    }
    return providerMap[paymentMethod];
};
const getPaymentMethodSettings = (provider, paymentMethod, paymentMethodsSettings)=>{
    if (!paymentMethodsSettings) {
        return undefined;
    }
    const paymentMethods = paymentMethodsSettings[provider];
    if (!paymentMethods) {
        return undefined;
    }
    return paymentMethods[paymentMethod];
};
const paymentMethodWithForm = (paymentMethod)=>{
    const paymentMethodsWithForm = [
        PaymentMethod.MBWAY,
        PaymentMethod.CREDITCARD,
        PaymentMethod.BANCONTACT_CARD,
        PaymentMethod.IDEAL,
        PaymentMethod.FAKE,
        PaymentMethod.ONEY,
        PaymentMethod.SEPA_DIRECT_DEBIT, 
    ];
    return paymentMethodsWithForm.includes(paymentMethod);
};

const usePaymentMethodForm = ()=>{
    const { config  } = useAdyen();
    const [selectedPaymentDetails, setSelectedPaymentDetails] = React.useState();
    const handlePaymentDataChange = React.useCallback((paymentData, isValid)=>{
        setSelectedPaymentDetails({
            data: paymentData,
            isValid
        });
    }, []);
    const resetPaymentDetails = React.useCallback(()=>{
        setSelectedPaymentDetails(undefined);
    }, []);
    const renderPaymentForm = React.useCallback((selectedPaymentMethod)=>{
        if (!selectedPaymentMethod) {
            return null;
        }
        const implementation = getPaymentImplementation(selectedPaymentMethod.provider, selectedPaymentMethod.type);
        // Get settings that are passed to payment settings provider from the OpCo site
        const settings = getPaymentMethodSettings(selectedPaymentMethod.provider, selectedPaymentMethod.type, config);
        if (!implementation || !implementation.form) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(implementation.form, {
            onChange: handlePaymentDataChange,
            config: selectedPaymentMethod.config,
            settings
        });
    }, [
        config,
        handlePaymentDataChange
    ]);
    return React.useMemo(()=>[
            renderPaymentForm,
            selectedPaymentDetails,
            resetPaymentDetails
        ]
    , [
        renderPaymentForm,
        selectedPaymentDetails,
        resetPaymentDetails
    ]);
};

var useGetPaymentMethodList = (cartId, onPaymentCompleteHandler)=>{
    const paymentMethodLabelsMap = usePaymentMethodLabelsMap();
    const [getPaymentMethods, queryResult] = useGetCartPaymentMethods();
    const { getAdditionalCosts , additionalCosts  } = useGetAdditionalCartCost();
    const { isProcessingOrder  } = usePaymentsUIContext();
    React.useEffect(()=>{
        if (cartId) {
            getPaymentMethods(cartId);
            getAdditionalCosts();
        }
    }, [
        cartId,
        getPaymentMethods,
        getAdditionalCosts
    ]);
    const [renderPaymentForm, selectedPaymentDetails, resetPaymentDetails] = usePaymentMethodForm();
    const paymentMethods = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.data;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.data
    ]);
    const error = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.error;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.error
    ]);
    const loading = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.loading;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.loading
    ]);
    const called = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.called;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.called
    ]);
    const selectedPaymentData = React.useMemo(()=>selectedPaymentDetails
    , [
        selectedPaymentDetails
    ]);
    const onHandlePaymentAction = React.useCallback(()=>{
        onPaymentCompleteHandler(selectedPaymentDetails);
    }, [
        onPaymentCompleteHandler,
        selectedPaymentDetails
    ]);
    const paymentMethodItems = React.useMemo(()=>{
        if (!paymentMethods || error) {
            return [];
        }
        const items = paymentMethods.map((_param)=>{
            var { provider , name , icons  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
                "provider",
                "name",
                "icons"
            ]);
            var ref, ref1;
            const mappedApiName = getRestPaymentMethodName(name);
            let item = React.Children.toArray(icons) // Call toArray so we can manipulate the data with type safety
            ;
            // Asides are provided as an array of thumbnail images for example.
            // Loop over additionalCosts to bind them to the aside array and return the combination.
            if (additionalCosts && additionalCosts.length) {
                additionalCosts.forEach((additionalCost, i)=>{
                    if (additionalCost.key === mappedApiName) {
                        const price = get(additionalCost, 'masterVariant.prices[0].value.centAmount', 0);
                        if (price) {
                            item = [
                                /*#__PURE__*/ React.createElement("span", {
                                    key: `${additionalCost.key}-aside-${i}`,
                                    className: "checkout-payment-methods__additional-cost"
                                }, "+ ", /*#__PURE__*/ React.createElement(Price, {
                                    value: price
                                })),
                                ...item !== null && item !== void 0 ? item : [], 
                            ];
                        }
                    }
                });
            }
            let formConfig;
            const paymentMethodList = (ref = rest) === null || ref === void 0 ? void 0 : ref.items;
            const cardBrands = (ref1 = rest) === null || ref1 === void 0 ? void 0 : ref1.brands;
            if (paymentMethodList) {
                formConfig = {
                    items: paymentMethodList
                };
            }
            if (cardBrands) {
                formConfig = {
                    brands: cardBrands
                };
            }
            const formComponent = renderPaymentForm({
                type: name,
                provider,
                config: formConfig
            });
            return {
                value: name,
                label: paymentMethodLabelsMap[name],
                id: `${provider}-${name}`,
                disabled: isProcessingOrder,
                aside: item,
                content: /*#__PURE__*/ React.createElement(PaymentForm, {
                    paymentMethod: name,
                    form: formComponent,
                    onHandlePaymentAction: onHandlePaymentAction,
                    isActionDisabled: false,
                    isActionLoading: false
                })
            };
        });
        return items;
    }, [
        paymentMethods,
        error,
        additionalCosts,
        renderPaymentForm,
        paymentMethodLabelsMap,
        onHandlePaymentAction,
        isProcessingOrder, 
    ]);
    return [
        React.useMemo(()=>paymentMethodItems
        , [
            paymentMethodItems
        ]),
        selectedPaymentData,
        resetPaymentDetails,
        {
            error,
            called,
            loading,
            data: paymentMethods || []
        }, 
    ];
};

const QrCode$1 = ({ className , externalUrl , isOpen , isQrCodeExpired , isQrRefreshLoading , labels , logoSettings , onCancel , qrCodeUrl , refreshQrCode  })=>{
    return /*#__PURE__*/ React__default.createElement(Modal, {
        isOpen: isOpen,
        className: classNames('qr-code-modal', className),
        title: labels.title,
        isOutsideClickEnabled: false
    }, /*#__PURE__*/ React__default.createElement("div", null, logoSettings && /*#__PURE__*/ React__default.createElement("img", {
        src: logoSettings.url,
        alt: logoSettings.alt
    })), /*#__PURE__*/ React__default.createElement("div", {
        className: "qr-code-modal__qr-code"
    }, /*#__PURE__*/ React__default.createElement(Image, {
        alt: "qr code",
        url: qrCodeUrl,
        cover: true,
        className: classNames({
            'qr-code-modal__is-expired': isQrCodeExpired
        })
    }, isQrCodeExpired && /*#__PURE__*/ React__default.createElement(Button, {
        "data-t": "refresh-qr-code",
        icon: "returns-alt",
        caption: labels.refresh,
        className: "qr-code-modal__refresh-button",
        onClick: ()=>refreshQrCode()
        ,
        isLoading: isQrRefreshLoading
    }))), /*#__PURE__*/ React__default.createElement("div", null, /*#__PURE__*/ React__default.createElement(Link, {
        className: "qr-code-modal__redirect-link",
        url: externalUrl,
        openInNewWindow: true
    }, labels.link)), /*#__PURE__*/ React__default.createElement("p", {
        className: "qr-code-modal__description"
    }, labels.description), /*#__PURE__*/ React__default.createElement(Button, {
        caption: labels.cancel,
        variant: "outline",
        "data-t": "qrcode-cancel-payment",
        className: "qr-code-modal__button",
        onClick: onCancel
    }));
};

const QrCodeContainer = ({ paymentMethodType , logoSettings , orderId , paymentResponse , paymentFlow , orderToken , onQrCodeRefresh , refetch , cart  })=>{
    const { getLabel  } = useIntl();
    const [paymentState, error] = usePaymentState(orderId);
    const { className , externalUrl , isOpen , isQrCodeExpired , isQrRefreshLoading , onCancel , qrCodeUrl ='' , refreshQrCode ,  } = useQrPaymentState({
        orderId,
        paymentState,
        error,
        paymentResponse,
        paymentFlow,
        orderToken,
        onQrCodeRefresh,
        cart,
        refetch
    });
    const labels = React.useMemo(()=>({
            cancel: getLabel(`checkout.payment.method.${paymentMethodType}.qr.action.cancel`),
            title: getLabel(`checkout.payment.method.${paymentMethodType}.qr.title`),
            refresh: getLabel(`checkout.payment.method.${paymentMethodType}.qr.action.refresh`),
            link: getLabel(`checkout.payment.method.${paymentMethodType}.qr.action.link`),
            description: getLabel(`checkout.payment.method.${paymentMethodType}.qr.description`)
        })
    , [
        getLabel,
        paymentMethodType
    ]);
    return /*#__PURE__*/ React.createElement(QrCode$1, {
        className: className,
        externalUrl: externalUrl,
        isOpen: isOpen,
        isQrCodeExpired: isQrCodeExpired,
        isQrRefreshLoading: isQrRefreshLoading,
        labels: labels,
        logoSettings: logoSettings,
        onCancel: onCancel,
        qrCodeUrl: qrCodeUrl,
        refreshQrCode: refreshQrCode
    });
};

const QrCode = (props)=>/*#__PURE__*/ React.createElement(QrCodeContainer, swcHelpers.extends({}, props))
;

const useRenderQrForm = ()=>{
    const renderQrForm = React.useCallback(({ paymentFlow , paymentMethodType , logoUrl , orderId , paymentResponse , orderToken , handleRefreshQrCode , cart , refetch , selfUrl ,  })=>{
        return /*#__PURE__*/ React.createElement(QrCode, {
            logoSettings: {
                alt: paymentMethodType,
                url: logoUrl
            },
            paymentMethodType: paymentMethodType,
            orderId: orderId,
            paymentResponse: paymentResponse,
            paymentFlow: paymentFlow,
            orderToken: orderToken,
            onQrCodeRefresh: handleRefreshQrCode,
            selfUrl: selfUrl,
            refetch: refetch,
            cart: cart
        });
    }, []);
    return React.useMemo(()=>[
            renderQrForm
        ]
    , [
        renderQrForm
    ]);
};

const PaymentMethodsUI = ({ handleChange , paymentMethodItems , selectedPaymentMethodType  })=>{
    return /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement(RadioButtonGroupWithContent, {
        scope: "paymentMethod",
        items: paymentMethodItems,
        value: selectedPaymentMethodType,
        onChange: handleChange
    }));
};

const PaymentMethodsUIContainer = ({ paymentMethodName , onPaymentMethodSelected , onHandlePayment , cartId , addAdditionalPaymentCosts , qrCodeForm , orderInProgress  })=>{
    const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState('');
    const { setIsProcessingOrder  } = usePaymentsUIContext();
    const [renderQrForm] = useRenderQrForm();
    React.useEffect(()=>{
        setIsProcessingOrder(orderInProgress);
    // We only need to listen for change in orderInProgress
    // adding setIsProcessingOrder in dep will result in extra re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        orderInProgress
    ]);
    const handleQrRefresh = React.useCallback(()=>{
        onHandlePayment({
            type: selectedPaymentMethod,
            isValid: true,
            data: {}
        });
    }, [
        onHandlePayment,
        selectedPaymentMethod
    ]);
    /**
   * QR scanner component when user selects QR supported payment method
   */ const qrComponent = React.useMemo(()=>{
        return qrCodeForm.paymentFlow === PaymentFlow.QR_CODE && qrCodeForm.paymentResponse && qrCodeForm.orderToken ? renderQrForm(swcHelpers.objectSpread({}, qrCodeForm, {
            handleRefreshQrCode: handleQrRefresh,
            cart: cartId ? {
                id: cartId
            } : null
        })) : null;
    }, [
        handleQrRefresh,
        qrCodeForm,
        renderQrForm,
        cartId
    ]);
    const isPaymentActionValid = React.useCallback((paymentConfig)=>{
        if (!paymentConfig) {
            return paymentMethodWithForm(selectedPaymentMethod) ? false : true;
        } else {
            return Boolean(paymentConfig === null || paymentConfig === void 0 ? void 0 : paymentConfig.isValid);
        }
    }, [
        selectedPaymentMethod
    ]);
    const onPaymentAction = React.useCallback((paymentConfig)=>{
        onHandlePayment(swcHelpers.objectSpread({}, paymentConfig, {
            isValid: isPaymentActionValid(paymentConfig),
            type: selectedPaymentMethod
        }));
    }, [
        onHandlePayment,
        selectedPaymentMethod,
        isPaymentActionValid
    ]);
    const [paymentMethodItems, _, resetPaymentDetails, { loading , data: paymentMethods  }] = useGetPaymentMethodList(cartId, onPaymentAction);
    const handleChange = React.useCallback((selectedPaymentMethodType)=>{
        const selectedPaymentMethodConfig = paymentMethods.filter((item)=>item.name == selectedPaymentMethodType
        )[0];
        onPaymentMethodSelected({
            type: selectedPaymentMethodConfig.name,
            provider: selectedPaymentMethodConfig.provider
        });
        resetPaymentDetails();
        // Handle logic for cash on delivery here
        paymentMethodName !== selectedPaymentMethodType && addAdditionalPaymentCosts({
            type: selectedPaymentMethodType,
            provider: selectedPaymentMethodConfig.provider
        });
        setSelectedPaymentMethod(selectedPaymentMethodType);
    }, [
        paymentMethods,
        onPaymentMethodSelected,
        resetPaymentDetails,
        paymentMethodName,
        addAdditionalPaymentCosts, 
    ]);
    React.useEffect(()=>{
        if (paymentMethods.length && selectedPaymentMethod === '' && paymentMethodName.length) {
            handleChange(paymentMethodName);
        }
    // excluding  handleChange from dep to prevent extra re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        paymentMethodName,
        paymentMethods,
        selectedPaymentMethod
    ]);
    if (!paymentMethodItems.length || loading) {
        return /*#__PURE__*/ React.createElement(Loader, null);
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(PaymentMethodsUI, {
        handleChange: handleChange,
        paymentMethodItems: paymentMethodItems,
        selectedPaymentMethodType: selectedPaymentMethod
    }), qrComponent);
};

const useRenderPaymentForm = ()=>{
    const { config  } = useAdyen();
    const [selectedPaymentDetails, setSelectedPaymentDetails] = React.useState();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState();
    const handlePaymentDataChange = React.useCallback((paymentData, isValid)=>{
        if (!selectedPaymentMethod) {
            return;
        }
        setSelectedPaymentDetails({
            data: paymentData,
            type: selectedPaymentMethod.type,
            isValid
        });
    }, [
        selectedPaymentMethod
    ]);
    const onPaymentMethodChanged = React.useCallback((paymentMethod)=>{
        setSelectedPaymentMethod(paymentMethod);
    }, []);
    const renderPaymentForm = React.useMemo(()=>{
        if (!selectedPaymentMethod) {
            return null;
        }
        const implementation = getPaymentImplementation(selectedPaymentMethod.provider, selectedPaymentMethod.type);
        // Get settings that are passed to payment settings provider from the OpCo site
        const settings = getPaymentMethodSettings(selectedPaymentMethod.provider, selectedPaymentMethod.type, config);
        if (!implementation || !implementation.form) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(implementation.form, {
            onChange: handlePaymentDataChange,
            details: selectedPaymentDetails && selectedPaymentDetails.data,
            config: selectedPaymentMethod.config,
            settings
        });
    }, [
        config,
        selectedPaymentDetails,
        selectedPaymentMethod,
        handlePaymentDataChange
    ]);
    return React.useMemo(()=>[
            renderPaymentForm,
            selectedPaymentDetails,
            onPaymentMethodChanged
        ]
    , [
        renderPaymentForm,
        selectedPaymentDetails,
        onPaymentMethodChanged
    ]);
};

const useGoToCheckoutLink = (cartId)=>{
    const { onClick: navigateLogin , href: hrefLogin  } = useLink({
        route: 'checkout.login'
    }) || {};
    const { onClick: navigateShipping , href: hrefShipping  } = useLink({
        route: 'checkout.shipping'
    }) || {};
    const hrefWithCartId = appendQueryParamToUrl(hrefLogin || hrefShipping || '/checkout/shipping', swcHelpers.objectSpread({}, cartId ? {
        cartId
    } : null));
    if (!hrefLogin && !hrefShipping) {
        console.error('This opco is not setup properly. You need to have at least the shipping page configured.');
    }
    return {
        onClick: navigateLogin || navigateShipping,
        href: hrefWithCartId
    };
};

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  useGetCartPaymentMethods: useGetCartPaymentMethods,
  useGetCartExpressPaymentMethods: useGetCartExpressPaymentMethods,
  useIsApplePayActive: useIsApplePayActive,
  useIsApplePaySupportedForCart: useIsApplePaySupportedForCart,
  usePaymentMethodLabelsMap: usePaymentMethodLabelsMap,
  useGetAdditionalCartCost: useGetAdditionalCartCost,
  useRenderPaymentForm: useRenderPaymentForm,
  useRenderQrForm: useRenderQrForm,
  useGoToCheckoutLink: useGoToCheckoutLink,
  useCheckout: useCheckout
});

export { ActionContainer, AdyenBrands, AdyenPaymentType, AdyenProvider, ApplePayButton, ApplePayExpressCheckoutButtonContainer, CreditCardContainer as CreditCard, ExpressPaymentMethod, index as Hooks, IdealContainer as Ideal, MbwayContainer as Mbway, OneyContainer as Oney, PaymentFlow, PaymentInstructionLayout, PaymentMethod, PaymentMethodFormContainer as PaymentMethodForm, PaymentMethodsUIContainer as PaymentMethodsUI, PaymentServiceProvider, PaymentsUIContextProvider, PaypalActionButton, SepaContainer as Sepa, getBankIcon, getBrandIcon, getCreditCardBrandsIcons, getPaymentImplementation, getPaymentMethodIcon, getPaymentMethodSettings, paymentMethodWithForm, useAdyen, useGetPaymentMethodList, usePaymentMethodForm, usePaymentState, usePaymentsUIContext, useQrPaymentState };
//# sourceMappingURL=index.js.map
